/** Lightweight WebAudio SFX synthesizer — no external assets required. */
export class AudioSynth {
  private ctx: AudioContext | null = null;
  private compressor: DynamicsCompressorNode | null = null;
  enabled = true;
  volume = 1;

  private ensure(): AudioContext | null {
    if (!this.enabled) return null;
    if (!this.ctx) {
      try {
        this.ctx = new AudioContext();
        this.compressor = this.ctx.createDynamicsCompressor();
        this.compressor.threshold.value = -18;
        this.compressor.knee.value = 18;
        this.compressor.ratio.value = 4;
        this.compressor.attack.value = 0.003;
        this.compressor.release.value = 0.16;
        this.compressor.connect(this.ctx.destination);
      } catch {
        return null;
      }
    }
    if (this.ctx.state === 'suspended') void this.ctx.resume();
    return this.ctx;
  }

  unlock(): void {
    this.ensure();
  }

  private tone(
    freq: number,
    dur: number,
    type: OscillatorType = 'square',
    gain = 0.04,
    slideTo?: number,
  ): void {
    const ctx = this.ensure();
    if (!ctx) return;
    const t = ctx.currentTime;
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(freq, t);
    if (slideTo) osc.frequency.exponentialRampToValueAtTime(Math.max(20, slideTo), t + dur);
    g.gain.setValueAtTime(gain * this.volume, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + dur);
    osc.connect(g).connect(this.compressor ?? ctx.destination);
    osc.start(t);
    osc.stop(t + dur + 0.02);
  }

  private noise(dur: number, gain = 0.05, lowpass = 800): void {
    const ctx = this.ensure();
    if (!ctx) return;
    const t = ctx.currentTime;
    const buf = ctx.createBuffer(1, ctx.sampleRate * dur, ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / data.length);
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = lowpass;
    const g = ctx.createGain();
    g.gain.value = gain * this.volume;
    src.connect(filter).connect(g).connect(this.compressor ?? ctx.destination);
    src.start(t);
  }

  // ---- Weapon-specific ammunition sounds --------------------------------

  /** Cyclone gatling — rapid mechanical burst. */
  gatling(): void {
    this.tone(1500, 0.025, 'square', 0.014, 900);
    this.noise(0.03, 0.02, 2600);
  }

  /** Heavy autocannon round. */
  autocannon(): void {
    // Layer a short mechanical transient over the low body so the round reads
    // as a weapon report instead of a single synthetic beep.
    this.tone(320, 0.06, 'square', 0.03, 120);
    this.tone(1850, 0.018, 'triangle', 0.018, 900);
    this.noise(0.07, 0.035, 1400);
  }

  /** Coherent laser discharge. */
  laserFire(): void {
    this.tone(2400, 0.14, 'sine', 0.028, 320);
    this.tone(1200, 0.1, 'triangle', 0.016, 200);
  }

  /** Plasma lance ignition. */
  plasmaFire(): void {
    this.tone(700, 0.16, 'sawtooth', 0.03, 180);
    this.noise(0.12, 0.02, 900);
  }

  /** Frost mortar — cold airy launch. */
  frostFire(): void {
    this.noise(0.22, 0.035, 1600);
    this.tone(900, 0.18, 'sine', 0.02, 300);
  }

  /** Pyre thermal projector — hotter and more sustained than cryo. */
  flameFire(): void {
    this.noise(0.14, 0.05, 1250);
    this.tone(260, 0.16, 'sawtooth', 0.025, 95);
    this.tone(720, 0.09, 'triangle', 0.018, 320);
  }

  /** Cluster bomb rack launch. */
  clusterLaunch(): void {
    this.tone(140, 0.18, 'sawtooth', 0.05, 60);
    this.noise(0.2, 0.06, 500);
  }

  /** Guided missile launch — rising motor note with a mechanical kick. */
  missileLaunch(): void {
    this.tone(210, 0.2, 'sawtooth', 0.035, 620);
    this.noise(0.16, 0.035, 900);
  }

  /** Tesla arc crackle. */
  teslaFire(): void {
    this.noise(0.06, 0.03, 5200);
    this.tone(1800, 0.05, 'square', 0.014, 700);
  }

  /** Small arms round striking armour / ground. */
  bulletImpact(): void {
    this.noise(0.05, 0.028, 2400);
    this.tone(260, 0.04, 'square', 0.02, 140);
  }

  /** Artillery shell detonation. */
  shellImpact(): void {
    this.noise(0.3, 0.09, 380);
    this.tone(70, 0.22, 'sawtooth', 0.06, 30);
  }

  shoot(): void {
    this.tone(880, 0.04, 'square', 0.02, 400);
  }
  flame(): void {
    this.noise(0.08, 0.03, 1200);
  }
  artillery(): void {
    this.noise(0.25, 0.08, 300);
    this.tone(80, 0.2, 'sawtooth', 0.05, 40);
    this.tone(520, 0.08, 'triangle', 0.02, 110);
  }

  /** Arrival sound selected by projectile family. */
  projectileImpact(kind: string): void {
    if (kind === 'shell' || kind === 'missile' || kind === 'cluster') this.shellImpact();
    else if (kind === 'flame') this.noise(0.05, 0.018, 1800);
    else if (kind === 'frost') {
      this.tone(1150, 0.08, 'sine', 0.018, 520);
      this.noise(0.06, 0.012, 2600);
    } else if (kind === 'plasma' || kind === 'beam') this.tone(980, 0.08, 'sine', 0.022, 180);
    else this.bulletImpact();
  }
  energy(): void {
    this.tone(1400, 0.12, 'sine', 0.03, 200);
  }
  explosion(): void {
    // Three restrained layers: low pressure, granular fire and a delayed
    // shockwave.  The delay keeps impacts readable without the old expanding
    // concentric-circle feel being echoed in audio.
    this.noise(0.35, 0.1, 400);
    this.tone(60, 0.25, 'sawtooth', 0.06, 30);
    this.noise(0.12, 0.045, 3200);
    setTimeout(() => this.tone(118, 0.18, 'sine', 0.035, 42), 70);
  }
  click(): void {
    this.tone(1200, 0.03, 'square', 0.02);
  }
  place(): void {
    this.tone(400, 0.08, 'triangle', 0.04, 700);
  }
  upgrade(): void {
    this.tone(500, 0.06, 'triangle', 0.04);
    setTimeout(() => this.tone(750, 0.1, 'triangle', 0.04), 60);
  }
  deny(): void {
    this.tone(180, 0.12, 'sawtooth', 0.04, 100);
  }
  breach(): void {
    this.tone(120, 0.3, 'sawtooth', 0.07, 60);
    this.noise(0.2, 0.05, 200);
  }
  wave(): void {
    this.tone(220, 0.15, 'square', 0.03, 440);
  }
  victory(): void {
    [440, 554, 659, 880].forEach((f, i) => setTimeout(() => this.tone(f, 0.25, 'triangle', 0.05), i * 120));
  }
  scoreTick(): void {
    this.tone(720, 0.025, 'square', 0.012, 980);
  }
  scoreCommit(): void {
    [520, 780, 1040].forEach((f, i) => setTimeout(() => this.tone(f, 0.1, 'triangle', 0.028), i * 70));
  }
  leaderboard(): void {
    this.tone(920, 0.08, 'sine', 0.022, 1320);
    this.tone(460, 0.12, 'triangle', 0.018, 680);
  }
  defeat(): void {
    [300, 220, 160, 100].forEach((f, i) => setTimeout(() => this.tone(f, 0.35, 'sawtooth', 0.05), i * 160));
  }
  special(): void {
    this.tone(200, 0.2, 'sawtooth', 0.05, 1200);
    this.tone(400, 0.25, 'square', 0.04, 80);
  }
}

export const audio = new AudioSynth();
